import { useEffect, useMemo, useState } from "react";
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
} from "@mui/material";
import api from "../../../utils/api";

const STATUS_LABELS = {
    planned: "Planned",
    in_progress: "In progress",
    done: "Done",
    canceled: "Canceled",
};

export default function DeleteBatchLessonsModal({
    open,
    onClose,
    classrooms = [],
    defaultClassroomId = "",
}) {
    const [classroomId, setClassroomId] = useState("");
    const [loadingStats, setLoadingStats] = useState(false);
    const [stats, setStats] = useState(null);
    const [keepDone, setKeepDone] = useState(true);
    const [confirmDestructive, setConfirmDestructive] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;
        setClassroomId(defaultClassroomId ? String(defaultClassroomId) : "");
        setStats(null);
        setKeepDone(true);
        setConfirmDestructive(false);
        setDeleting(false);
        setError("");
    }, [open, defaultClassroomId]);

    useEffect(() => {
        if (!open) return;
        if (!classroomId) {
            setStats(null);
            return;
        }
        let cancelled = false;
        const load = async () => {
            try {
                setLoadingStats(true);
                setError("");
                const res = await api.get(`admin/classrooms/${classroomId}/lessons/stats`);
                if (cancelled) return;
                setStats(res.data?.data || null);
            } catch (e) {
                if (cancelled) return;
                setError(e?.response?.data?.message || "Failed to load lesson stats.");
                setStats(null);
            } finally {
                if (!cancelled) setLoadingStats(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [open, classroomId]);

    const total = stats?.total ?? 0;
    const byStatus = stats?.by_status || {};
    const doneCount = Number(byStatus.done || 0);

    const lessonsThatWillBeDeleted = useMemo(() => {
        if (!stats) return 0;
        return keepDone ? Math.max(0, total - doneCount) : total;
    }, [stats, total, doneCount, keepDone]);

    const hasDoneWarning = doneCount > 0;

    // Require explicit confirmation if user is about to delete already-completed lessons.
    const needsConfirm = hasDoneWarning && !keepDone;
    const canDelete = !!classroomId
        && !deleting
        && !loadingStats
        && lessonsThatWillBeDeleted > 0
        && (!needsConfirm || confirmDestructive);

    const handleDelete = async () => {
        if (!classroomId) return;
        try {
            setDeleting(true);
            setError("");
            await api.delete(`admin/classrooms/${classroomId}/lessons`, {
                params: keepDone ? { keep_done: 1 } : {},
            });
            onClose(true);
        } catch (e) {
            setError(e?.response?.data?.message || "Failed to delete lessons.");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <Dialog open={open} onClose={() => { if (!deleting) onClose(); }} maxWidth="sm" fullWidth>
            <DialogTitle>Delete batch lessons</DialogTitle>
            <DialogContent>
                <Box display="flex" flexDirection="column" gap={2} mt={1}>
                    <Typography variant="body2" color="text.secondary">
                        Pick a classroom to remove all of its lessons. You can keep the lessons that are already
                        marked <strong>Done</strong> if you only want to clear future ones.
                    </Typography>

                    <FormControl>
                        <FormLabel htmlFor="delete-batch-classroom">Classroom</FormLabel>
                        <Select
                            id="delete-batch-classroom"
                            value={classroomId}
                            onChange={(e) => setClassroomId(e.target.value)}
                        >
                            <MenuItem value="">
                                <em>Choose classroom…</em>
                            </MenuItem>
                            {classrooms.map((c) => (
                                <MenuItem key={c.id} value={String(c.id)}>
                                    {c.name || c.classroom_name || `Classroom #${c.id}`}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {classroomId ? (
                        loadingStats ? (
                            <Stack direction="row" alignItems="center" gap={1}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" color="text.secondary">
                                    Counting lessons…
                                </Typography>
                            </Stack>
                        ) : stats ? (
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 1,
                                    border: 1,
                                    borderColor: "divider",
                                    bgcolor: "background.paper",
                                }}
                            >
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Lessons in this classroom: <strong>{total}</strong>
                                </Typography>
                                {total === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                        No lessons to delete.
                                    </Typography>
                                ) : (
                                    <Stack spacing={0.5}>
                                        {Object.entries(STATUS_LABELS).map(([key, label]) => (
                                            <Stack
                                                key={key}
                                                direction="row"
                                                justifyContent="space-between"
                                                sx={{ fontSize: 13 }}
                                            >
                                                <span>{label}</span>
                                                <strong>{Number(byStatus[key] || 0)}</strong>
                                            </Stack>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        ) : null
                    ) : null}

                    {hasDoneWarning ? (
                        <Alert severity="warning" sx={{ mb: 0 }}>
                            <AlertTitle>Done lessons exist</AlertTitle>
                            This classroom has <strong>{doneCount}</strong> lesson(s) already marked{" "}
                            <strong>Done</strong>. By default they are kept.
                            <Box sx={{ mt: 1 }}>
                                <FormControlLabel
                                    sx={{ m: 0 }}
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={keepDone}
                                            onChange={(e) => {
                                                setKeepDone(e.target.checked);
                                                if (e.target.checked) setConfirmDestructive(false);
                                            }}
                                        />
                                    }
                                    label="Keep Done lessons (recommended)"
                                />
                            </Box>
                            {!keepDone ? (
                                <Box sx={{ mt: 1 }}>
                                    <FormControlLabel
                                        sx={{ m: 0 }}
                                        control={
                                            <Checkbox
                                                size="small"
                                                color="error"
                                                checked={confirmDestructive}
                                                onChange={(e) => setConfirmDestructive(e.target.checked)}
                                            />
                                        }
                                        label={`I understand this will also delete the ${doneCount} Done lesson(s)`}
                                    />
                                </Box>
                            ) : null}
                        </Alert>
                    ) : null}

                    {error ? <FormHelperText error>{error}</FormHelperText> : null}

                    {classroomId && stats && total > 0 ? (
                        <Typography variant="body2">
                            Lessons that will be deleted:{" "}
                            <strong>{lessonsThatWillBeDeleted}</strong>
                            {keepDone && doneCount > 0 ? (
                                <Typography component="span" variant="body2" color="text.secondary">
                                    {" "}
                                    ({doneCount} Done lesson(s) kept)
                                </Typography>
                            ) : null}
                        </Typography>
                    ) : null}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()} disabled={deleting}>
                    Cancel
                </Button>
                <Button
                    color="error"
                    variant="contained"
                    onClick={handleDelete}
                    disabled={!canDelete}
                >
                    {deleting ? (
                        <CircularProgress size={20} />
                    ) : lessonsThatWillBeDeleted > 0 ? (
                        `Delete ${lessonsThatWillBeDeleted} lesson(s)`
                    ) : (
                        "Delete"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
